import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface SiteData {
  hero: {
    badge: string;
    title1: string;
    title2: string;
    description: string;
    stat1Number: string;
    stat1Label: string;
    stat2Number: string;
    stat2Label: string;
    stat3Number: string;
    stat3Label: string;
    stat4Number: string;
    stat4Label: string;
  };
  services: {
    web: { title: string; description: string; features: string[] };
    ses: { title: string; description: string; features: string[] };
    canli: { title: string; description: string; features: string[] };
  };
  portfolio: Array<{
    id: number;
    title: string;
    domain: string;
    description: string;
    tags: string[];
    image?: string;
  }>;
  videos: Array<{
    id: number;
    title: string;
    description: string;
    url: string;
  }>;
  contact: {
    phone: string;
    email: string;
    location: string;
    workingHours: string;
  };
  about: {
    title: string;
    description1: string;
    description2: string;
    description3: string;
    stat1Number: string;
    stat1Label: string;
    stat2Number: string;
    stat2Label: string;
    stat3Number: string;
    stat3Label: string;
    stat4Number: string;
    stat4Label: string;
    skills: Array<{ icon: string; title: string; desc: string }>;
  };
}

const defaultData: SiteData = {
  hero: {
    badge: "Web Tasarım & Sesli Yazıya Çeviri Uzmanı",
    title1: "Dijital dünyada",
    title2: "fark yaratıyoruz",
    description:
      "Profesyonel web tasarım hizmetleri ve dünya şampiyonu sesli yazıya çeviri uzmanlığı ile işinizi bir üst seviyeye taşıyoruz. Konferanslarınızda Canlı Altyazı hizmetimizle kulaklık sorununu ortadan kaldırıyor, konuşulanların anında altyazı ile ekranda görünmesini sağlıyoruz.",
    stat1Number: "5+",
    stat1Label: "Tamamlanan Web Sitesi",
    stat2Number: "7",
    stat2Label: "Dünya Şampiyonluğu",
    stat3Number: "20+",
    stat3Label: "Sesi Yazıya Çevirme",
    stat4Number: "20+",
    stat4Label: "Canlı Altyazı",
  },
  services: {
    web: {
      title: "Web Tasarım",
      description:
        "Modern, responsive ve kullanıcı dostu web siteleri tasarlıyorum. İşinizin dijital vitrini en iyi şekilde yansısın.",
      features: ["Responsive tasarım", "Modern arayüz", "SEO uyumlu yapı", "Hızlı yüklenme"],
    },
    ses: {
      title: "Sesli Yazıya Çeviri",
      description:
        "Dünya şampiyonu hassasiyetinde ses kayıtlarını yazıya çeviriyorum. Podcaster'lar, medya şirketleri ve hukuk büroları için ideal.",
      features: ["Yüksek doğruluk oranı", "Hızlı teslimat", "Çoklu dil desteği", "Gizlilik garantisi"],
    },
    canli: {
      title: "Canlı Altyazı",
      description:
        "Etkinlikler, konferanslar ve canlı yayınlar için gerçek zamanlı altyazı hizmeti sunuyorum.",
      features: ["Gerçek zamanlı çeviri", "Etkinlik deneyimi", "Profesyonel ekipman", "Esnek programlama"],
    },
  },
  portfolio: [
    {
      id: 1,
      title: "Vize Bilgi Merkezi",
      domain: "vizebilgimerkezi.com",
      description: "React + TypeScript ile geliştirilen, 7 ülke için detaylı vize rehberleri sunan premium üyelik platformu.",
      tags: ["React", "TypeScript", "Premium Üyelik", "Admin Panel"],
    },
  ],
  videos: [
    {
      id: 1,
      title: "Canlı Altyazi Calismasi",
      description: "Kongre'de yabanci konuklarin konusmasi canli altyazi olarak ekrana aktarildi.",
      url: "",
    },
  ],
  contact: {
    phone: "0850 XXX XX XX",
    email: "info@hkdesign.com",
    location: "Türkiye",
    workingHours: "Pazartesi - Cuma: 09:00 - 18:00",
  },
  about: {
    title: "Merhaba, ben Hakan",
    description1: "Web tasarım ve dijital çözümler alanında çalışmalarımı sürdürüyorum.",
    description2: "Aynı zamanda sesli yazıya çevirme konusunda dünya klavye şampiyonuyum.",
    description3: "Her projede müşteri memnuniyetini ön planda tutarak çalışıyorum.",
    stat1Number: "5+", stat1Label: "Tamamlanan Web Sitesi",
    stat2Number: "7", stat2Label: "Dünya Şampiyonluğu",
    stat3Number: "20+", stat3Label: "Sesi Yazıya Çevirme",
    stat4Number: "20+", stat4Label: "Canlı Altyazı",
          skills: [
            { icon: "🎨", title: "Web Tasarim", desc: "Modern, responsive web siteleri" },
            { icon: "🎙️", title: "Sesi Yayiya Ceviri", desc: "Dunya sampiyonu hassasiyet" },
            { icon: "📺", title: "Canli Altyazi", desc: "Gercek zamanli altyazi hizmeti" },
            { icon: "⚛️", title: "React & TypeScript", desc: "Modern web uygulamalari gelistirme" },
          ],
  },
};

interface SiteDataContextType {
  data: SiteData;
}

const SiteDataContext = createContext<SiteDataContextType>({ data: defaultData });

export const SiteDataProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<SiteData>(defaultData);

  useEffect(() => {
    const saved = localStorage.getItem("hkdesign_data");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (!parsed.videos) parsed.videos = [];
      if (!parsed.about) {
        parsed.about = defaultData.about;
      }
      setData(parsed);
    }

    // dinleme: storage değişikliğinde güncelle
    const handleStorage = () => {
      const updated = localStorage.getItem("hkdesign_data");
      if (updated) {
        setData(JSON.parse(updated));
      }
    };
    window.addEventListener("storage", handleStorage);

    // polling ile kontrol et (aynı sekme için)
    const interval = setInterval(() => {
      const updated = localStorage.getItem("hkdesign_data");
      if (updated) {
        const parsed = JSON.parse(updated);
        setData((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
            return parsed;
          }
          return prev;
        });
      }
    }, 1000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, []);

  return (
    <SiteDataContext.Provider value={{ data }}>
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => useContext(SiteDataContext);
